/**
 * Environment gate for the GTM container.
 *
 * ‼ PREVIEW AND LOCAL DEPLOYS MUST NOT FIRE PRODUCTION TAGS.
 *
 * GTM-WS5BKHBX holds the live Google Ads conversion tags, the Meta Pixel, GA4 and
 * Clarity. They are configured inside the container, not in this codebase, so
 * gating whether the container loads at all is the single choke point for every
 * one of them.
 *
 * This is not hypothetical. The consumer site (medicare-advocates-web) shipped the
 * ungated version and a QA run through the funnel on a preview URL posted a REAL
 * conversion into the live Ads account, where it fed Smart Bidding. Their 2026-09-29
 * measurement audit found the Ads Conversion Linker had accumulated 49 auto-link
 * domains, 48 of them preview hosts — GTM only learns a domain by observing its own
 * container running there, so that is 48 preview deployments that fired live tags.
 *
 * ‼ NOTE THE DIRECTION OF THE TEST, AND DO NOT "SIMPLIFY" IT to
 *     vercelEnv === "production"
 * That reads better and is dangerous. `NEXT_PUBLIC_VERCEL_ENV` only exists while
 * Vercel's "Automatically expose System Environment Variables" is enabled. If that
 * is ever switched off, the positive test resolves false IN PRODUCTION and silently
 * strips every tag from the live site — all analytics and all conversion tracking,
 * with nothing visibly broken. That is far worse than the bug it fixes.
 *
 * So: disable only when we can POSITIVELY identify a non-production environment.
 * A missing variable falls back to today's behaviour, never to a dark site.
 *
 * `nodeEnv` covers local `npm run dev`, where no Vercel variable exists at all.
 * It is set by Next itself and is never "development" in a production build, so
 * it adds local safety without putting production at risk.
 */
export function tagsEnabledFor(
    vercelEnv: string | undefined,
    nodeEnv: string | undefined,
): boolean {
    if (nodeEnv === "development") return false;
    return vercelEnv !== "preview" && vercelEnv !== "development";
}

/** Whether the GTM container should load in the current environment. */
export function tagsEnabled(): boolean {
    return tagsEnabledFor(
        process.env.NEXT_PUBLIC_VERCEL_ENV,
        process.env.NODE_ENV,
    );
}

export const GTM_ID = "GTM-WS5BKHBX";
